<template>
  <v-card outlined class="rounded-lg">
    <v-card-title class="subtitle-1 font-weight-bold">
      Ranking
      <v-spacer />
      <v-select v-model="roleFilter" :items="roleOptions" label="Role" clearable dense outlined hide-details class="mr-2" style="max-width:200px" />
      <v-select v-model="companyFilter" :items="companyOptions" label="Company" clearable dense outlined hide-details class="mr-2" style="max-width:200px" />
      <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="Search" dense outlined hide-details clearable class="mr-2" style="max-width:220px" />
      <v-btn small outlined color="primary" class="text-none" :disabled="!filtered.length" @click="exportCsv">
        <v-icon small left>mdi-download</v-icon>Export CSV
      </v-btn>
    </v-card-title>
    <v-data-table
      :headers="headers"
      :items="filtered"
      :loading="loading"
      :items-per-page="25"
      sort-by="rank"
      class="clickable-rows"
      @click:row="$emit('select', $event)"
    >
      <template #[`item.name`]="{ item }">
        <div class="font-weight-medium">{{ item.name }}</div>
        <div class="caption grey--text">{{ item.role || '—' }}<span v-if="item.roles && item.roles.length"> · {{ item.roles.join(', ') }}</span><span v-if="item.companies && item.companies.length"> · {{ item.companies.join(', ') }}</span></div>
      </template>
      <template #[`item.growth_score`]="{ item }">
        <v-chip small :color="scoreColor(item.growth_score)" dark>{{ item.growth_score }}</v-chip>
      </template>
      <template v-for="p in pillars" #[`item.${p.key}`]="{ item }">
        <v-progress-linear :key="p.key" :value="item[p.key]" :color="p.color" height="8" rounded style="min-width:60px" />
      </template>
      <template #[`item.trend`]="{ item }">
        <span :class="item.trend > 0 ? 'success--text' : item.trend < 0 ? 'error--text' : 'grey--text'">
          <v-icon small :color="item.trend > 0 ? 'success' : item.trend < 0 ? 'error' : 'grey'">
            {{ item.trend > 0 ? 'mdi-arrow-up' : item.trend < 0 ? 'mdi-arrow-down' : 'mdi-minus' }}
          </v-icon>{{ Math.abs(item.trend) }}
        </span>
      </template>
      <template #no-data>No employees found.</template>
    </v-data-table>
  </v-card>
</template>

<script>
import { buildCsv, csvFilename } from './csv'

export default {
  name: 'AdminRankingTable',
  props: {
    rows: { type: Array, default: () => [] },
    loading: Boolean,
    range: { type: Object, default: () => ({ from: '', to: '' }) },
  },
  data() {
    return {
      search: '',
      roleFilter: null,
      companyFilter: null,
      pillars: [
        { key: 'kpi_score', color: 'primary' },
        { key: 'task_score', color: 'teal' },
        { key: 'ai_score', color: 'purple' },
        { key: 'habit_score', color: 'orange' },
      ],
      headers: [
        { text: '#', value: 'rank', width: 50 },
        { text: 'Employee', value: 'name' },
        { text: 'Level', value: 'level_name' },
        { text: 'Score', value: 'growth_score' },
        { text: 'KPI', value: 'kpi_score', sortable: false },
        { text: 'Tasks', value: 'task_score', sortable: false },
        { text: 'AI', value: 'ai_score', sortable: false },
        { text: 'Habits', value: 'habit_score', sortable: false },
        { text: 'Trend', value: 'trend' },
        { text: 'Praise', value: 'recognition_received' },
      ],
    }
  },
  computed: {
    roleOptions() {
      return [...new Set(this.rows.flatMap(r => r.roles || []))].sort()
    },
    companyOptions() {
      return [...new Set(this.rows.flatMap(r => r.companies || []))].sort()
    },
    filtered() {
      const q = (this.search || '').trim().toLowerCase()
      return this.rows.filter(r => {
        if (this.roleFilter && !(r.roles || []).includes(this.roleFilter)) return false
        if (this.companyFilter && !(r.companies || []).includes(this.companyFilter)) return false
        if (!q) return true
        const hay = [r.name, r.role, ...(r.roles || []), ...(r.companies || [])].filter(Boolean).join(' ').toLowerCase()
        return hay.includes(q)
      })
    },
  },
  methods: {
    exportCsv() {
      const rows = [...this.filtered].sort((a, b) => a.rank - b.rank)
      const blob = new Blob([buildCsv(rows)], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = csvFilename(this.range)
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
    scoreColor(s) {
      return s >= 70 ? 'success' : s >= 40 ? 'warning' : 'grey'
    },
  },
}
</script>

<style scoped>
.clickable-rows ::v-deep tbody tr { cursor: pointer; }
</style>
